import { Component, h, JSX } from 'preact';
import Layer from '../layer';
import Controls from '../controls';
import VideoSkin from '../video-skin';
import style from './index.css';
import {
  sendAnalyticsTrack,
  sendCloseSKOverlay,
  sendCreativeClientIgnoredClick,
  sendCreativeDragged,
  sendErrorSKOverlay,
  sendOpenSKOverlay
} from '../../../../common/js/TrackAnalyticsHelper';
import { VastIconType } from '../../lib/vast/vast-icon';
import { addEventListener } from '../../../../common/js/vpaid/vpaid';
import { isMediumRectangleAdUnit } from '../../../../common/js/adUnit';
import {
  getOgAdContext,
  isAndroid,
  isWeb
} from '../../../../common/js/environment-utils';
import { getCurrentRenderTime } from '../../../../common/js/render-timer';
import { RootStore } from '../../../../common/js/stores/root.store';
import { EndScreenOrchestrator } from '../../lib/end-screen/orchestrator';
import {
  EndScreenSkipType,
  EndScreenType,
  EndScreenTypeValue
} from '../../lib/end-screen/end-screen-types';
import { VideoStore } from '../../../../common/js/stores/video.store';
import { reaction } from '../../../../common/js/utils/preact-utils';
import * as vpaid from '../../../../common/js/vpaid/vpaid';
import { AdContainerStore } from '../../../../common/js/stores/ad-container.store';
import ConfirmOverlay from '../../../../common/components/confirm-overlay';
import { withTranslator } from '../../../../common/components/translator-provider';
import EndScreenVideo from '../../../../common/components/endscreen-video';
import {
  DisplayTrackers,
  DisplayTrackersProps
} from '../../../../common/components/display-trackers';

export interface VideoOverlayProps {
  elapsedTime: number;
  videoDuration: number;
  isVideoMuted: boolean;
  adUnit: AdUnitType;
  mediaFile: any;
  showOptoutLink: boolean;
  vastIcon?: VastIconType;
  childPrivacyApplied: boolean;
  skanActivated: boolean;
  isOA: boolean;
  displayTrackers: DisplayTrackersProps;
  interaction: string;
  ignoreClicksBefore: number;
  forceClickOnTouch: boolean;
  isRewardVideo: boolean;
  isRewardOnLaunch: boolean;
  showRecommendation: boolean;
  cta: any;
  closeBtn: any;
  video: any;
  skipAfterDelay: any;
  confirmSkip: any;
  skOverlay: any;
  colors: any;
  canInstallIcon: boolean;
  translate: (key: string) => string;
  onClick: (
    source: string,
    event?: MouseEvent | MessageEvent,
    url?: string,
    handledByPlayer?: boolean
  ) => void;
  onOptout: (e: Event) => void;
  onToggleSound: (isMuted: boolean) => void;
  onEvent: (eventName: string) => void;
  onSkinRendered: () => void;
  onToggleExpand: (e: Event) => void;
  onClickVastIcon: (url: string) => void;
  skip: () => void;
  onSkipVideo: () => void;
  onClose: () => void;
  onAutoClose: () => void;
  onForceClose: (e: Event) => void;
}

interface VideoOverlayState {
  renderedAt: number;
  wasSkipAfterDelayShownThisPlay: boolean;
  showSkipAfterDelay: boolean;
  showConfirmSkip: boolean;
  autoCloseTimerElapsingMilliseconds: number;
  lastClickTime: number;
  showEndScreenClose: boolean;
  storeKitOpened: boolean;
  storeKitHasBeenOpened: boolean;
  skOverlayHasBeenOpened: boolean;
  skOverlayIsOpened: boolean;
  clickCloseTime?: number;
  closingScheduledIn?: number;
  alreadyClosed?: boolean;
}

export class VideoOverlay extends Component<
  VideoOverlayProps,
  VideoOverlayState
> {
  static defaultProps = {
    hideCloseButton: false,
    onSkinRendered: () => {},
    onEvent: () => {}
  };

  skipLayerType: string;
  skipLayerZonePrefix: string;
  forceConfirmSkip: boolean;
  autoCloseTimer: number | false = false;
  autoCloseDone: boolean;
  cancelClose?: () => void;
  isMediumRectangleAd: boolean;
  videoStore: VideoStore;
  fsContainerElement: HTMLElement | null = null;
  adContainerStore: AdContainerStore;

  constructor(props: VideoOverlayProps) {
    super(props);

    this.isMediumRectangleAd = isMediumRectangleAdUnit(this.props.adUnit);

    this.onToggleMute = this.onToggleMute.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onClickFromOverlay = this.onClickFromOverlay.bind(this);
    this.onClickFromVpaid = this.onClickFromVpaid.bind(this);
    this.attemptClose = this.attemptClose.bind(this);
    this.attemptForceClose = this.attemptForceClose.bind(this);
    this.showConfirmSkip = this.showConfirmSkip.bind(this);
    this.handleLongPress = this.handleLongPress.bind(this);
    this.handleDrag = this.handleDrag.bind(this);
    this.skipLayerType = this.props.isRewardVideo ? 'optIn' : 'confirmSkip';
    this.skipLayerZonePrefix = this.props.isRewardVideo
      ? 'reward'
      : 'confirm_skip';
    this.forceConfirmSkip = false;
    this.autoCloseDone = false;

    this.state = {
      renderedAt: Date.now(),
      wasSkipAfterDelayShownThisPlay: false,
      showSkipAfterDelay: false,
      showConfirmSkip: false,
      autoCloseTimerElapsingMilliseconds: 0,
      lastClickTime: 0,
      showEndScreenClose: false,
      storeKitOpened: false,
      storeKitHasBeenOpened: false,
      skOverlayHasBeenOpened: false,
      skOverlayIsOpened: false
    };

    const rootStore = RootStore.getInstance();
    this.videoStore = rootStore.videoStore;
    this.adContainerStore = rootStore.adContainerStore;

    this.listenClickFromVpaid();
    this.setupStoreObservers();
  }

  get isVpaid(): boolean {
    return this.videoStore.isVpaid;
  }

  setupStoreObservers(): void {
    const isVideoCompletedDestroyObserver = reaction(
      () => this.videoStore.isVideoCompleted.value,
      (isVideoCompleted: boolean) => {
        if (!isVideoCompleted) return;

        this.setLastClickTime();

        if (!this.isMediumRectangleAd && !this.autoCloseDone) {
          this.resumeAutoCloseTimer();
        }

        isVideoCompletedDestroyObserver && isVideoCompletedDestroyObserver();
      }
    );

    reaction(
      () => this.adContainerStore.isViewable.value,
      this.handleIsViewable.bind(this)
    );
  }

  handleIsViewable(isViewable: boolean): void {
    if (isViewable) {
      this.setLastClickTime();
    }

    if (
      this.isMediumRectangleAd ||
      this.autoCloseDone ||
      !this.videoStore.isVideoCompleted.value
    )
      return;

    if (isViewable) {
      this.resumeAutoCloseTimer();
    } else {
      this.pauseAutoCloseTimer();
    }
  }

  listenClickFromVpaid(): void {
    addEventListener('AdClickThru', this.onClickFromVpaid);
  }

  onClickFromVpaid(vpaidUrl: string = '', playerHandles?: boolean): void {
    let url: string | undefined;

    if (this.props.isOA && !vpaidUrl && typeof playerHandles === 'undefined') {
      playerHandles = false;
    } else if (!isWeb()) {
      playerHandles = true;
    }

    if (playerHandles && vpaidUrl) {
      url = vpaidUrl;
    }

    this.onClick('video', undefined, url, playerHandles);
  }

  onClickFromOverlay(
    source: string = 'video',
    event?: MessageEvent | MouseEvent
  ): void {
    if (this.isVpaid && this.fsContainerElement) {
      return vpaid.clickAd(this.fsContainerElement);
    }

    this.onClick(source, event);
  }

  onClick(
    source: string = 'video',
    event?: MouseEvent | MessageEvent,
    url: string = '',
    handledByPlayer?: boolean
  ): void {
    const endScreen = EndScreenOrchestrator.instance.endScreen;
    if (
      source === 'video' &&
      this.videoStore.isVideoCompleted.value &&
      endScreen
    ) {
      source = 'endScreen';
    }

    sendAnalyticsTrack(event || {}, source, true);

    const isCtaFullScreen =
      this.props.cta &&
      this.props.cta.useCta &&
      this.videoStore.isVideoExpanded.value;

    const isRenderTimedOut =
      this.props.ignoreClicksBefore > 0 &&
      getCurrentRenderTime() < this.props.ignoreClicksBefore;

    const isIgnoredClick =
      source !== 'skipLayer' &&
      isRenderTimedOut &&
      !isCtaFullScreen &&
      !this.isVpaid;

    if (isIgnoredClick) {
      sendCreativeClientIgnoredClick(event);
      sendAnalyticsTrack(event || {}, 'ignored_format_interaction', false);
      return;
    }

    this.cancelClose && this.cancelClose();

    this.tryDisplayStoreKit();

    this.props.onClick(source, event, url, handledByPlayer);
  }

  onClose(): void {
    if (this.state.clickCloseTime) {
      return;
    }

    const timeToClose = Math.min(
      this.props.closeBtn.closeDelay + 1,
      Math.max(0, this.props.videoDuration - this.props.elapsedTime)
    );
    this.setState({
      clickCloseTime: this.props.elapsedTime,
      closingScheduledIn: this.videoStore.isVideoCompleted.value
        ? 0
        : timeToClose
    });

    if ((this.state.closingScheduledIn || 0) <= 0) {
      this.props.skip();
      return;
    }

    this.cancelClose = () => {
      this.setState({
        clickCloseTime: undefined,
        closingScheduledIn: undefined
      });
    };
  }

  getChildContext() {
    return {
      translate: this.props.translate
    };
  }

  componentWillReceiveProps(nextProps: VideoOverlayProps): void {
    if (
      !this.state.wasSkipAfterDelayShownThisPlay &&
      !this.state.showSkipAfterDelay &&
      nextProps.elapsedTime < this.props.videoDuration &&
      nextProps.elapsedTime >= this.props.skipAfterDelay.delay
    ) {
      this.showSkipAfterDelay(true);
    }
  }

  showConfirmSkip(show: boolean): void {
    if (show) {
      this.props.onEvent('onskipconfirmation_display');
    }

    this.setState({
      showConfirmSkip: show
    });
  }

  hideSkipAfterDelay(): void {
    this.setState({
      showSkipAfterDelay: false
    });
  }

  showSkipAfterDelay(show: boolean): void {
    this.setState(prevState => {
      return {
        showSkipAfterDelay:
          show &&
          !prevState.clickCloseTime &&
          !prevState.showConfirmSkip &&
          this.props.skipAfterDelay &&
          this.props.skipAfterDelay.enabled,
        wasSkipAfterDelayShownThisPlay: true
      };
    });

    if (this.state.showSkipAfterDelay) {
      this.props.onEvent('layerafterdelay_display');
    }
  }

  attemptClose(event: MouseEvent): void {
    if (
      !this.videoStore.isVideoCompleted.value &&
      EndScreenOrchestrator.instance.hasNext
    ) {
      EndScreenOrchestrator.instance.skip(EndScreenSkipType.MANUAL);
      this.props.onSkipVideo();
      this.setState({ autoCloseTimerElapsingMilliseconds: 0 });
      return;
    }

    if (EndScreenOrchestrator.instance.endScreen) {
      EndScreenOrchestrator.instance.skip(EndScreenSkipType.MANUAL);
      this.setState({ autoCloseTimerElapsingMilliseconds: 0 });
      if (EndScreenOrchestrator.instance.endScreen) {
        return;
      }
    }

    const isSkipAllowed =
      this.props.confirmSkip &&
      this.props.confirmSkip.enabled &&
      !this.state.clickCloseTime;

    const isRewarded =
      this.props.isRewardVideo &&
      (this.props.isRewardOnLaunch ||
        this.props.video.billedAfter - this.props.elapsedTime <= 0);

    const isDefaultControls =
      !(this.props.closeBtn && this.props.closeBtn.useCrossCloseButton) &&
      !(this.props.cta && this.props.cta.useCta);

    if (isSkipAllowed && (isDefaultControls || isRewarded)) {
      if (isRewarded) {
        this.skipLayerType = 'confirmSkip';
        this.forceConfirmSkip = true;
      }
      sendAnalyticsTrack(event);
      this.showConfirmSkip(true);
    } else {
      sendAnalyticsTrack(event, 'continue_button');
      this.onClose();
    }
  }

  attemptForceClose(event: MouseEvent): void {
    if (!this.state.clickCloseTime) {
      this.showConfirmSkip(false);
    }
    sendAnalyticsTrack(event);
    this.onClose();
  }

  checkAutoClose(): void {
    const { onAutoClose } = this.props;
    const endScreen = EndScreenOrchestrator.instance.endScreen;
    const duration = (endScreen && endScreen.duration) || 0;

    if (duration === Infinity) return this.pauseAutoCloseTimer();

    if (
      !this.state.storeKitOpened &&
      duration * 1000 < this.state.autoCloseTimerElapsingMilliseconds
    ) {
      EndScreenOrchestrator.instance.skip(EndScreenSkipType.AUTO);
      this.setState({ autoCloseTimerElapsingMilliseconds: 0 });
      if (!EndScreenOrchestrator.instance.endScreen) {
        this.autoCloseDone = true;
        this.pauseAutoCloseTimer();
        onAutoClose();
      }
    }
  }

  checkToShowEndScreenClose(): void {
    const endScreen = EndScreenOrchestrator.instance.endScreen;
    const showCloseDelay =
      (endScreen && endScreen.closeBtn && endScreen.closeBtn.delay) || 0;
    if (
      !this.state.showEndScreenClose &&
      showCloseDelay * 1000 <= this.state.autoCloseTimerElapsingMilliseconds
    ) {
      this.setState({ showEndScreenClose: true });
    } else if (
      this.state.showEndScreenClose &&
      showCloseDelay * 1000 > this.state.autoCloseTimerElapsingMilliseconds
    ) {
      this.setState({ showEndScreenClose: false });
    }
  }

  tryDisplayStoreKit(): boolean {
    if (
      this.state.storeKitOpened ||
      !RootStore.getInstance().adContainerStore.canOpenStoreKit({
        adUnitType: this.props.adUnit,
        skan: this.props.skanActivated
      })
    ) {
      return false;
    }
    this.setState({
      storeKitOpened: true,
      storeKitHasBeenOpened: true,
      skOverlayIsOpened: false
    });
    this.listenCloseStoreKitEvent();
    return true;
  }

  tryDisplaySKOverlay(): void {
    // Guard: skip automatic SKOverlay display when disabled by ms-bidder.
    // Safe here because tryDisplaySKOverlay is only called from auto-trigger paths (componentDidUpdate).
    const { skoverlay } = getOgAdContext();
    if (skoverlay?.autoclick === 0) return;

    const shouldDisplaySKOverlay =
      !this.state.skOverlayHasBeenOpened &&
      !this.state.storeKitHasBeenOpened &&
      RootStore.getInstance().adContainerStore.canOpenSKOverlay({
        adUnitType: this.props.adUnit,
        skan: this.props.skanActivated
      }) &&
      this.props.elapsedTime > this.props.skOverlay.displayAfter;

    if (!shouldDisplaySKOverlay) return;

    this.setState({ skOverlayIsOpened: true, skOverlayHasBeenOpened: true });
    this.listenCloseSKOverlayEvent();
    sendOpenSKOverlay();
    this.adContainerStore.adContainerPort.ogyOpenSKOverlay();
    this.props.onClick('skoverlay');
  }

  listenCloseStoreKitEvent(): void {
    const closeCallback = () => {
      this.adContainerStore.adContainerPort.removeEventListener(
        'ogyCloseStoreKit',
        closeCallback
      );
      this.setState({ storeKitOpened: false });
    };
    this.adContainerStore.adContainerPort.addEventListener(
      'ogyCloseStoreKit',
      closeCallback
    );
  }

  listenCloseSKOverlayEvent(): void {
    const closeCallback = (errorCode?: number) => {
      this.adContainerStore.adContainerPort.removeEventListener(
        'ogyUserCloseSKOverlay',
        closeCallback
      );
      this.setState({ skOverlayIsOpened: false });
      const hasErrorCode = errorCode !== undefined;
      if (!hasErrorCode) {
        sendCloseSKOverlay();
        return;
      }
      sendErrorSKOverlay(errorCode);
    };
    this.adContainerStore.adContainerPort.addEventListener(
      'ogyUserCloseSKOverlay',
      closeCallback
    );
  }

  onToggleMute(): void {
    const isVideoMuted = !this.props.isVideoMuted;

    this.props.onToggleSound(isVideoMuted);
  }

  handleLongPress(): void {
    this.props.onEvent('pressed');
  }

  handleDrag(): void {
    if (isWeb() && isAndroid() && this.props.forceClickOnTouch) {
      this.onClickFromOverlay();
    } else {
      sendCreativeDragged();
    }
  }

  resumeAutoCloseTimer(): void {
    if (!this.autoCloseTimer) {
      this.autoCloseTimer = window.setInterval(() => {
        this.setState(prevState => {
          return {
            autoCloseTimerElapsingMilliseconds:
              prevState.autoCloseTimerElapsingMilliseconds + 100
          };
        });
      }, 100);
    }
  }

  pauseAutoCloseTimer(): void {
    if (this.autoCloseTimer) {
      window.clearInterval(this.autoCloseTimer);
    }
    this.autoCloseTimer = false;
  }

  componentDidUpdate(): void {
    if (
      !this.isMediumRectangleAd &&
      !this.autoCloseDone &&
      this.videoStore.isVideoCompleted.value
    ) {
      const { storekit } = getOgAdContext();
      if (
        storekit?.autoclick !== 0 &&
        !this.state.storeKitHasBeenOpened &&
        this.tryDisplayStoreKit()
      ) {
        this.props.onClick('endScreenStoreKit');
      }
      this.checkAutoClose();
      this.checkToShowEndScreenClose();
    }

    if (
      !this.state.alreadyClosed &&
      this.state.clickCloseTime &&
      (this.props.elapsedTime - this.state.clickCloseTime >
        (this.state.closingScheduledIn || 0) ||
        this.videoStore.isVideoCompleted.value)
    ) {
      this.setState({ alreadyClosed: true });
      this.props.skip();
    }
    this.tryDisplaySKOverlay();
  }

  getClickVideo(): () => void {
    const { cta } = this.props;

    const shouldResetFade =
      cta && cta.useCta && this.videoStore.isVideoExpanded.value;
    if (shouldResetFade) {
      return this.setLastClickTime.bind(this);
    }

    if (this.isDisplayingHtmlEndScreen()) {
      return () => {};
    }

    return () => this.onClickFromOverlay('video');
  }

  setLastClickTime(): void {
    this.setState({ lastClickTime: this.props.elapsedTime });
  }

  isDisplayingHtmlEndScreen(): boolean {
    const endScreen = EndScreenOrchestrator.instance.endScreen;
    return !!(
      endScreen &&
      ([
        EndScreenType.HTML,
        EndScreenType.IFRAME
      ] as EndScreenTypeValue[]).indexOf(endScreen.type) >= 0
    );
  }

  canDisplaySkinOrEndscreen(): boolean {
    if (this.isMediumRectangleAd && !EndScreenOrchestrator.instance.endScreen) {
      return false;
    }

    return !!(
      EndScreenOrchestrator.instance.endScreen ||
      this.videoStore.shouldDisplaySkin()
    );
  }

  isVpaidNormalInteractions(): boolean {
    return (
      this.isVpaid &&
      !(
        this.videoStore.isVideoExpanded.value &&
        this.props.cta &&
        this.props.cta.useCta
      )
    );
  }

  canDisplaySkipLayerVideo(): boolean {
    return (
      this.videoStore.isVideoExpanded.value &&
      (this.state.showSkipAfterDelay || this.state.showConfirmSkip)
    );
  }

  shouldDisplayEndScreenVideoMPU(): boolean {
    return (
      this.isMediumRectangleAd &&
      !EndScreenOrchestrator.instance.endScreen &&
      this.props.elapsedTime > this.props.videoDuration - 2
    );
  }

  render(): JSX.Element {
    const {
      skipAfterDelay,
      confirmSkip,
      videoDuration,
      interaction
    } = this.props;
    const endScreen = EndScreenOrchestrator.instance.endScreen;

    const mediaFile = this.videoStore.mediaFile.value;

    const clickHandler =
      interaction === 'click' ? this.getClickVideo() : () => {};
    const touchHandler =
      interaction !== 'click' ? this.getClickVideo() : () => {};

    const containerStyle =
      this.isVpaidNormalInteractions() || this.isDisplayingHtmlEndScreen()
        ? { pointerEvents: 'none' as const }
        : { pointerEvents: 'auto' as const };

    return (
      <div
        id="video_fs_container"
        ref={el => (this.fsContainerElement = el)}
        className={style.videoOverlayContainer}
        data-zone="video-overlay"
        style={containerStyle}
        onTouchStart={this.handleLongPress}
        onTouchMove={this.handleDrag}
      >
        {this.shouldDisplayEndScreenVideoMPU() ? (
          <EndScreenVideo
            src={mediaFile?.url}
            mediaType={mediaFile?.type}
            videoCurrentTime={videoDuration - 1}
            renderType={'MPU'}
          />
        ) : (
          ''
        )}
        {this.canDisplaySkinOrEndscreen() && (
          <Layer zIndex={101} dataZone="skin">
            <VideoSkin
              videoDuration={videoDuration}
              isMediumRectangleAdUnit={this.isMediumRectangleAd}
              isVideoCompleted={
                this.videoStore.isVideoCompleted.value &&
                !this.state.clickCloseTime
              }
              onRendered={this.props.onSkinRendered}
              endScreen={
                this.isMediumRectangleAd ||
                this.videoStore.isVideoCompleted.value
                  ? endScreen
                  : undefined
              }
              onClick={this.onClickFromOverlay}
            />
          </Layer>
        )}

        <Controls
          props={this.props as any}
          onClickVideo={clickHandler}
          onTouchStartVideo={touchHandler}
          setLastClickTime={this.setLastClickTime.bind(this)}
          lastClickTime={this.state.lastClickTime}
          closingScheduledIn={this.state.closingScheduledIn}
          clickCloseTime={this.state.clickCloseTime}
          onToggleMute={this.onToggleMute}
          attemptClose={this.attemptClose}
          onClick={this.onClickFromOverlay}
          attemptForceClose={this.attemptForceClose}
          showEndScreenClose={this.state.showEndScreenClose}
          skOverlayIsOpened={this.state.skOverlayIsOpened}
        />
        {this.canDisplaySkipLayerVideo() ? (
          <Layer zIndex={103} dataZone="layer" key={'layer-skip'}>
            {!this.state.showConfirmSkip && this.state.showSkipAfterDelay ? (
              <ConfirmOverlay
                {...skipAfterDelay}
                name="after_delay"
                message={this.props.translate('skipOrContinue.message')}
                confirm={this.props.translate('skipOrContinue.confirm')}
                cancel={this.props.translate('skipOrContinue.cancel')}
                onConfirm={(e: MouseEvent, duration: number) => {
                  e.stopPropagation();
                  this.props.onEvent(
                    `layerafterdelay_click_skip#{"duration": ${duration}}`
                  );
                  sendAnalyticsTrack(e, 'layer_skip');
                  this.onClose();
                }}
                onCancel={(e: MouseEvent, duration: number) => {
                  e.stopPropagation();
                  this.props.onEvent(
                    `layerafterdelay_click_continue#{"duration": ${duration}}`
                  );
                  sendAnalyticsTrack(e);
                  this.showSkipAfterDelay(false);
                }}
                onClick={(e: MouseEvent) => {
                  sendAnalyticsTrack(e);
                }}
                position={'top'}
                skOverlayIsOpened={this.state.skOverlayIsOpened}
              />
            ) : (
              ''
            )}
            {!this.state.showSkipAfterDelay && this.state.showConfirmSkip ? (
              <ConfirmOverlay
                {...confirmSkip}
                name={this.skipLayerZonePrefix}
                message={this.props.translate(`${this.skipLayerType}.message`)}
                confirm={this.props.translate(`${this.skipLayerType}.confirm`)}
                cancel={this.props.translate(`${this.skipLayerType}.cancel`)}
                onConfirm={(e: MouseEvent, duration: number) => {
                  e.stopPropagation();
                  this.props.onEvent(
                    `onskipconfirmation_click_skip#{"duration": ${duration}}`
                  );
                  this.showConfirmSkip(false);
                  sendAnalyticsTrack(e, 'layer_skip');
                  this.onClose();
                }}
                onCancel={(e: MouseEvent, duration: number) => {
                  e.stopPropagation();
                  this.props.onEvent(
                    `onskipconfirmation_click_visit#{"duration": ${duration}}`
                  );
                  if (this.props.isRewardVideo && !this.forceConfirmSkip) {
                    sendAnalyticsTrack(e);
                    this.showConfirmSkip(false);
                  } else {
                    sendAnalyticsTrack(e, 'layer_visit_website', true);
                    this.props.onClick('skipLayer');
                  }
                }}
                onClick={(e: MouseEvent) => {
                  sendAnalyticsTrack(e);
                }}
                position={'bottom'}
                skOverlayIsOpened={this.state.skOverlayIsOpened}
              />
            ) : (
              ''
            )}
          </Layer>
        ) : null}
        {this.adContainerStore.isViewable.value ? (
          <DisplayTrackers {...this.props.displayTrackers} />
        ) : (
          ''
        )}
      </div>
    );
  }
}

export default withTranslator(VideoOverlay);
